import { ICourse } from '@/types/course';
import { PaymentInclude, PaymentType } from '@/types/payment';
import { IUser } from '@/types/user';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useGetPayments } from './usePayments';

export const useExportPayments = (
  taxBenefitsProviderId?: string,
  paymentType?: PaymentType,
) => {
  const [shouldFetchAllPayments, setShouldFetchAllPayments] = useState(false);

  const { refetch: refetchAllPayments } = useGetPayments(
    {
      ...(taxBenefitsProviderId && {
        payer: taxBenefitsProviderId,
      }),
      ...(paymentType && { paymentType }),
      status: 'PENDING',
    },
    {
      page: 1,
      limit: 1000,
    },
    [PaymentInclude.Course, PaymentInclude.Order, PaymentInclude.User],
    { createdAt: 'desc' },
    {
      enabled: shouldFetchAllPayments,
    },
  );

  const exportPayments = async () => {
    setShouldFetchAllPayments(true);

    const result = await refetchAllPayments();

    if (!result.data?.data?.length) {
      enqueueSnackbar('No payments to export', {
        variant: 'error',
      });
      setShouldFetchAllPayments(false);
      return;
    }

    const allPayments = result.data.data;

    const headers = ['Employee', 'Course', 'Amount', 'Type', 'Date', 'Status'];

    const csv = `${headers.join(',')}\n`;

    const lines = allPayments.map(p =>
      [
        (p.user as IUser).email,
        (p.order?.item.course as ICourse).name,
        p.totalAmount,
        p.paymentResource,
        format(p.createdAt, 'MMMM yyyy'),
        p.status,
      ].join(','),
    );

    const filename = `pending-payments-${Date.now()}.csv`;
    const blob = new Blob([csv.concat(lines.join('\n'))], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setShouldFetchAllPayments(false);

    enqueueSnackbar('Pending payments successfully exported', {
      variant: 'success',
    });
  };

  return {
    exportPayments,
    isExporting: shouldFetchAllPayments,
  };
};
